import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { MessageCircle, Instagram, Facebook, CheckCircle, AlertCircle, Settings, Copy, ExternalLink, Loader2, ChevronRight, Send, UserPlus, MessageSquare } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MessengerSetup() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    enabled: false,
    page_id: '',
    page_access_token: '',
    instagram_account_id: '',
    auto_reply: true,
    use_ai: true
  });
  const [saving, setSaving] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('setup');
  const [addingToFollowUp, setAddingToFollowUp] = useState(false);
  const messagesEndRef = React.useRef(null);

  const webhookUrl = 'https://aimedplanner.com/api/webhook/messenger';
  const verifyToken = 'aimedplanner_messenger_verify';

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    fetchSettings();
    fetchConversations();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      console.log('📱 Fetching messenger settings for clinic:', clinicId);
      if (!clinicId) {
        console.error('❌ No clinicId found in localStorage');
        return;
      }
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Messenger settings loaded:', data);
        setSettings(prev => ({ ...prev, ...data }));
      } else {
        console.error('❌ Failed to fetch settings:', response.status);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Map API response to expected format - use sender_id for conversation grouping
        const mappedConvs = (data.conversations || []).map(c => ({
          ...c,
          id: c.sender_id || c.conversation_id || c.message_id || c.id,
          sender_id: c.sender_id,
          conversation_id: c.conversation_id,
          user_name: c.sender_name || 'Usuario',
          channel: c.platform || 'facebook',
          last_message: c.message || c.text || ''
        }));
        setConversations(mappedConvs);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-messages/${conversationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (response.ok) {
        alert('✅ Configuración guardada');
      }
    } catch (error) {
      alert('❌ Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copiado');
  };

  const selectConversation = (conv) => {
    setSelectedConv(conv);
    // Try sender_id first (more reliable), fallback to conversation_id or id
    const lookupId = conv.sender_id || conv.conversation_id || conv.id;
    fetchMessages(lookupId);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv) return;
    
    const messageText = newMessage.trim();
    
    // Optimistic update - show message immediately in UI
    const optimisticMsg = {
      id: `temp-${Date.now()}`,
      message: messageText,
      text: messageText,
      content: messageText,
      is_from_user: false,
      is_ai: false,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-send?sender_id=${selectedConv.sender_id}&message=${encodeURIComponent(messageText)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        // Refresh messages from server to get the real saved message
        const lookupId = selectedConv.sender_id || selectedConv.conversation_id || selectedConv.id;
        setTimeout(() => fetchMessages(lookupId), 500);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const addToFollowUp = async (conv) => {
    if (!conv) return;
    
    setAddingToFollowUp(true);
    try {
      const token = localStorage.getItem('token');
      const clinicId = localStorage.getItem('clinicId');
      
      const response = await fetch(`${API}/api/clinic/${clinicId}/messenger-add-to-followup`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sender_id: conv.sender_id,
          sender_name: conv.user_name || conv.sender_name
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`✅ ${data.lead_name} agregado a Follow-up!\nTeléfono: ${data.lead_phone}\nSe enviará mensaje Day 0.`);
        fetchConversations(); // Refresh to show "En Follow-up" badge
      } else if (data.error === 'no_phone') {
        const phone = prompt('No se encontró teléfono en la conversación.\nIngresa el teléfono manualmente:');
        if (phone) {
          // Retry with manual phone
          const retryResponse = await fetch(`${API}/api/clinic/${clinicId}/messenger-add-to-followup`, {
            method: 'POST',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              sender_id: conv.sender_id,
              sender_name: conv.user_name || conv.sender_name,
              phone: phone
            })
          });
          const retryData = await retryResponse.json();
          if (retryData.success) {
            alert(`✅ ${retryData.lead_name} agregado a Follow-up!`);
            fetchConversations();
          } else {
            alert(`❌ Error: ${retryData.message || 'No se pudo agregar'}`);
          }
        }
      } else if (data.error === 'already_in_followup') {
        alert(`ℹ️ Este lead ya está en Follow-up.\nEstado actual: ${data.existing_status}`);
      } else {
        alert(`❌ Error: ${data.message || 'No se pudo agregar a Follow-up'}`);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error al agregar a Follow-up');
    } finally {
      setAddingToFollowUp(false);
    }
  };

  return (
    <DashboardLayout title="Instagram & Facebook">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <Instagram className="w-6 h-6 text-white" />
              </div>
              Instagram & Facebook DMs
            </h1>
            <p className="text-gray-600 mt-1">Responde automáticamente mensajes de Instagram y Facebook</p>
          </div>
        </div>

        {/* Banner: Link a Comentarios IA */}
        <button
          onClick={() => navigate('/dashboard/social-comments')}
          className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-pink-50 to-orange-50 border border-pink-200 rounded-lg hover:bg-pink-100 transition-colors group"
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-pink-600" />
            <span className="text-sm text-gray-700">
              <strong>¿Buscas comentarios públicos?</strong> Ve a Comentarios IA
            </span>
          </div>
          <ChevronRight className="w-4 h-4 text-pink-400 group-hover:text-pink-600 transition-colors" />
        </button>

        {/* Tabs */}
        <div className="flex space-x-4 border-b">
          <button
            onClick={() => setActiveTab('setup')}
            className={`pb-3 px-4 font-medium ${activeTab === 'setup' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500'}`}
          >
            <Settings className="w-4 h-4 inline mr-2" />
            Configuración
          </button>
          <button
            onClick={() => setActiveTab('conversations')}
            className={`pb-3 px-4 font-medium ${activeTab === 'conversations' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500'}`}
          >
            <MessageCircle className="w-4 h-4 inline mr-2" />
            Conversaciones
          </button>
        </div>

        {activeTab === 'setup' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Setup Guide */}
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <h2 className="font-semibold text-lg">Guía de Configuración</h2>
              
              <div className="space-y-4">
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="font-medium text-purple-800 mb-2">1. Crear App en Meta for Developers</p>
                  <a
                    href="https://developers.facebook.com/apps"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline flex items-center"
                  >
                    developers.facebook.com/apps
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800 mb-2">2. Agregar producto "Messenger"</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>En tu app, busca "Messenger" y haz clic en "Configurar"</li>
                    <li>Conecta tu Página de Facebook</li>
                    <li>Genera un Page Access Token</li>
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800 mb-2">3. Configurar Instagram (opcional)</p>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    <li>Tu Instagram debe ser cuenta Profesional/Business</li>
                    <li>Debe estar conectado a tu Página de Facebook</li>
                    <li>Habilita "Permitir acceso a mensajes" en Instagram</li>
                  </ul>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-800 mb-2">4. Configurar Webhook</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500">URL del Webhook</label>
                      <div className="flex">
                        <code className="flex-1 px-3 py-2 bg-white border rounded-l text-sm">{webhookUrl}</code>
                        <button
                          onClick={() => copyToClipboard(webhookUrl)}
                          className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r hover:bg-gray-200"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Token de Verificación</label>
                      <div className="flex">
                        <code className="flex-1 px-3 py-2 bg-white border rounded-l text-sm">{verifyToken}</code>
                        <button
                          onClick={() => copyToClipboard(verifyToken)}
                          className="px-3 py-2 bg-gray-100 border border-l-0 rounded-r hover:bg-gray-200"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Suscribir campos: <code>messages</code>, <code>messaging_postbacks</code>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Form */}
            <div className="bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <h2 className="font-semibold text-lg">Credenciales</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Page ID (Facebook)</label>
                  <input
                    type="text"
                    value={settings.page_id || ''}
                    onChange={(e) => setSettings({...settings, page_id: e.target.value})}
                    placeholder="123456789012345"
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Page Access Token</label>
                  <input
                    type="text"
                    value={settings.page_access_token || ''}
                    onChange={(e) => setSettings({...settings, page_access_token: e.target.value})}
                    placeholder="EAAG..."
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-gray-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Account ID (opcional)</label>
                  <input
                    type="text"
                    value={settings.instagram_account_id || ''}
                    onChange={(e) => setSettings({...settings, instagram_account_id: e.target.value})}
                    placeholder="17841..."
                    autoComplete="off"
                    data-lpignore="true"
                    data-form-type="other"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-gray-900"
                  />
                  <p className="text-xs text-gray-500 mt-1">Solo si quieres responder DMs de Instagram</p>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Habilitar integración</p>
                    <p className="text-sm text-gray-500">Recibir y responder mensajes</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={(e) => setSettings({...settings, enabled: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">Respuestas con IA</p>
                    <p className="text-sm text-gray-500">La IA responde automáticamente</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.use_ai}
                      onChange={(e) => setSettings({...settings, use_ai: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>

                <button
                  onClick={saveSettings}
                  disabled={saving}
                  className="w-full py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center"
                >
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Guardar Configuración'}
                </button>
              </div>
            </div>

            {/* Meta Business AI Card - Full Width */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border p-6 space-y-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <Facebook className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-semibold text-lg">Meta Business AI</h2>
                  <p className="text-sm text-gray-600">Filtro inicial para Facebook e Instagram</p>
                </div>
              </div>

              {/* Explanation */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-800">
                  <strong>Meta Business AI</strong> puede responder dudas básicas en Facebook e Instagram, 
                  pero <strong>NO debe confirmar citas ni inventar horarios</strong>. Para agendar, reagendar, 
                  cancelar o confirmar disponibilidad, debe enviar al paciente a WhatsApp con Gina.
                </p>
              </div>

              {/* Warning Banner */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">⚠️ Advertencia importante</p>
                  <p className="text-amber-700 text-sm mt-1">
                    No permitas que Meta Business AI confirme citas. Las citas solo se confirman 
                    en AI MedPlanner/Gina. Si Meta AI confirma citas sin pasar por Gina, 
                    se pueden crear citas inexistentes.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://business.facebook.com/latest/inbox"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir Meta Business Suite
                </a>
                <a
                  href="https://wa.me/5215549168798?text=Hola,%20quiero%20agendar%20una%20valoración%20oftalmológica"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  Probar WhatsApp
                </a>
              </div>

              {/* Prompt Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-gray-700">Prompt recomendado para Meta Business AI</label>
                  <button
                    onClick={() => {
                      const prompt = document.getElementById('meta-ai-prompt').value;
                      navigator.clipboard.writeText(prompt);
                      const btn = document.getElementById('copy-prompt-btn');
                      const originalText = btn.innerText;
                      btn.innerText = '✓ Copiado';
                      btn.classList.add('bg-green-600');
                      btn.classList.remove('bg-gray-600');
                      setTimeout(() => {
                        btn.innerText = originalText;
                        btn.classList.remove('bg-green-600');
                        btn.classList.add('bg-gray-600');
                      }, 2000);
                    }}
                    id="copy-prompt-btn"
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copiar prompt
                  </button>
                </div>
                <textarea
                  id="meta-ai-prompt"
                  readOnly
                  className="w-full h-64 px-4 py-3 bg-gray-50 border rounded-lg text-sm font-mono text-gray-700 resize-none overflow-y-auto"
                  value={`Eres el asistente inicial de Optall Vision y del Dr. César Sánchez Galeana. Tu función es responder dudas generales, orientar al paciente y llevarlo al canal correcto. No eres el sistema de agenda definitivo.

REGLA PRINCIPAL: Nunca confirmes una cita. Nunca digas "tu cita quedó agendada", "te esperamos tal día", "ya quedó confirmado" ni frases equivalentes. Solo AI MedPlanner/Gina por WhatsApp puede confirmar una cita cuando exista disponibilidad real y se genere un registro interno.

Si una persona quiere:
- agendar cita
- cambiar una cita
- confirmar disponibilidad
- pedir valoración
- reagendar
- cancelar
- preguntar horarios específicos
- pedir que le aparten un lugar

debes enviarla a WhatsApp.

Respuesta recomendada:
"Para agendarte correctamente necesitamos revisar disponibilidad real en nuestro sistema. Escríbenos por WhatsApp aquí: https://wa.me/5215549168798?text=Hola,%20quiero%20agendar%20una%20valoración%20oftalmológica"

Si el paciente ya dio datos, resume antes de enviarlo:
- nombre
- teléfono
- servicio de interés
- fecha u horario deseado
- duda principal

Ejemplo:
"Perfecto, tienes interés en una valoración oftalmológica y prefieres esta semana. Para confirmar disponibilidad real y registrar tu cita, escríbenos por WhatsApp aquí: https://wa.me/5215549168798?text=Hola,%20quiero%20agendar%20una%20valoración%20oftalmológica"

Puedes responder dudas generales sobre:
- valoración oftalmológica
- cirugía láser
- catarata
- cirugía refractiva
- ojo seco
- lentes intraoculares
- ubicación general
- preparación general para consulta
- qué llevar a una valoración

No des diagnósticos definitivos. No sustituyas una consulta médica. No prometas resultados quirúrgicos. No des precios cerrados si no hay valoración. No inventes disponibilidad. No inventes promociones. No confirmes citas.

Si la persona describe síntomas urgentes como dolor intenso, pérdida súbita de visión, trauma ocular, secreción severa, visión doble nueva o pérdida visual repentina, responde:
"Eso requiere valoración médica prioritaria. Por favor escríbenos por WhatsApp de inmediato para orientarte: https://wa.me/5215549168798"

Tono: Profesional, cálido, breve y claro. No uses respuestas largas. Prioriza llevar a WhatsApp cuando haya intención de cita.`}
                />
                <p className="text-xs text-gray-500">
                  Copia este prompt y pégalo en la configuración de Meta Business AI en tu Business Suite.
                </p>
              </div>

              {/* Checklist */}
              <div className="space-y-3">
                <h3 className="font-medium text-gray-700">Checklist de configuración</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {[
                    'Abrir Meta Business Suite',
                    'Ir a Inbox / Bandeja de entrada',
                    'Activar Meta Business AI si está disponible',
                    'Pegar el prompt recomendado',
                    'Configurar respuestas para Facebook e Instagram',
                    'Probar con una conversación interna',
                    'Confirmar que Meta AI nunca agenda',
                    'Confirmar que redirige a WhatsApp 5549168798',
                    'Revisar que los mensajes importantes sigan entrando a AI MedPlanner'
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-sm">
                      <CheckCircle className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Badge */}
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                  <Facebook className="w-4 h-4" />
                  Meta AI = filtro inicial
                  <span className="mx-1">•</span>
                  <MessageCircle className="w-4 h-4" />
                  Gina = agenda real
                </span>
              </div>
            </div>
          </div>
        ) : (
          /* Conversations Tab */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Conversations List */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-4 border-b bg-gray-50">
                <h3 className="font-semibold text-gray-900">Conversaciones</h3>
                <p className="text-xs text-gray-500 mt-1">
                  {conversations.filter(c => c.is_new).length} nuevos • {conversations.filter(c => !c.is_new).length} contactados
                </p>
              </div>
              <div className="divide-y max-h-[500px] overflow-y-auto">
                {conversations.map(conv => (
                  <div
                    key={conv.id || conv.sender_id}
                    onClick={() => selectConversation(conv)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 ${selectedConv?.id === conv.id ? 'bg-purple-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${conv.channel === 'instagram' || conv.platform === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-blue-500'}`}>
                          {(conv.channel === 'instagram' || conv.platform === 'instagram') ? <Instagram className="w-5 h-5 text-white" /> : <Facebook className="w-5 h-5 text-white" />}
                        </div>
                        {conv.is_new && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{conv.user_name || conv.sender_name || 'Usuario'}</p>
                          {conv.is_new && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Nuevo</span>
                          )}
                          {conv.in_follow_up && (
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">En Follow-up</span>
                          )}
                          {conv.is_lead && !conv.in_follow_up && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Lead</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate">{conv.last_message || conv.message || ''}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-gray-400 capitalize">{conv.channel || conv.platform}</p>
                          {conv.message_count > 1 && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{conv.message_count} msgs</span>
                          )}
                          {conv.has_ai_response && (
                            <span className="text-xs">🤖</span>
                          )}
                          {/* Add to Follow-up button */}
                          {!conv.in_follow_up && (
                            <button
                              onClick={(e) => { e.stopPropagation(); addToFollowUp(conv); }}
                              disabled={addingToFollowUp}
                              className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full hover:bg-green-200 flex items-center gap-1"
                            >
                              <UserPlus className="w-3 h-3" />
                              Follow-up
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {conversations.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No hay conversaciones aún</p>
                  </div>
                )}
              </div>
            </div>

            {/* Chat View */}
            <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border flex flex-col h-[500px]">
              {selectedConv ? (
                <>
                  <div className="p-4 border-b flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedConv.channel === 'instagram' ? 'bg-gradient-to-r from-purple-500 to-pink-500' : 'bg-blue-500'}`}>
                        {selectedConv.channel === 'instagram' ? <Instagram className="w-5 h-5 text-white" /> : <Facebook className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <p className="font-medium">{selectedConv.user_name}</p>
                        <p className="text-xs text-gray-500 capitalize">{selectedConv.channel}</p>
                      </div>
                    </div>
                    {/* Add to Follow-up button in chat header */}
                    {!selectedConv.in_follow_up && (
                      <button
                        onClick={() => addToFollowUp(selectedConv)}
                        disabled={addingToFollowUp}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                      >
                        {addingToFollowUp ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserPlus className="w-4 h-4" />
                        )}
                        Agregar a Follow-up
                      </button>
                    )}
                    {selectedConv.in_follow_up && (
                      <span className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        En Follow-up
                      </span>
                    )}
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <p>No hay mensajes</p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => (
                        <div key={msg.id || idx} className={`flex ${msg.is_from_user ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[70%] px-4 py-2 rounded-2xl ${msg.is_from_user ? 'bg-white border text-gray-900' : 'bg-purple-600 text-white'}`}>
                            <p className="text-sm whitespace-pre-wrap">{msg.message || msg.text || msg.content || '[Sin contenido]'}</p>
                            <p className={`text-xs mt-1 ${msg.is_from_user ? 'text-gray-400' : 'opacity-70'}`}>
                              {msg.is_ai && '🤖 IA • '}
                              {msg.created_at && new Date(msg.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  <div className="p-4 border-t">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Escribe un mensaje..."
                        className="flex-1 px-4 py-2 border rounded-full focus:ring-2 focus:ring-purple-500"
                        onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      />
                      <button
                        onClick={sendMessage}
                        className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white hover:bg-purple-700"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Selecciona una conversación</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
